import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTourStore } from '@/stores/tour';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';

interface TourStep {
  targetSelector: string;
  title: string;
  body: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

// Written for non-technical users: short, plain sentences that say exactly what
// to click and what will happen. Any step whose anchor isn't on screen is
// skipped automatically, so it's safe to include every area here.
const CLIENT_STEPS: TourStep[] = [
  {
    targetSelector: '[data-tour="sidebar-home"]',
    title: '1. Welcome — this is your Home',
    body: 'This is your starting page. It shows the documents BillerBay has asked you for, anything due soon, and the files most recently shared with you. You can always come back here by clicking "Home" on the left.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="sidebar-to-do"]',
    title: '2. Requests — what BillerBay needs from you',
    body: 'When BillerBay needs a document, it appears here with a due date. Click a request to open it, then attach your file right inside it — no need to hunt for the right folder.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="sidebar-my-files"]',
    title: '3. Your files & folders',
    body: 'Everything you and BillerBay share lives here, sorted into folders (Claims, Remittances, and so on). Click a folder to open it, then click any file to view or download it.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="upload"]',
    title: '4. How to send a file (important)',
    body: 'To send billing or documents: 1) open a folder, 2) drag your files onto the page — or click here to browse and pick them. You can drop a whole folder at once. Wait for the green tick that means it uploaded.',
    position: 'bottom',
  },
  {
    targetSelector: '[data-tour="upload"]',
    title: '5. Where files must go',
    body: 'Always drop files inside a folder (for example "00 Migration" or "06 Claims") — not on the very top level. If you drop in the wrong place the app will tell you which folder to use.',
    position: 'bottom',
  },
  {
    targetSelector: '[data-tour="cmdk"]',
    title: '6. Search',
    body: 'Looking for a specific file? Click here (or press Ctrl+K) and start typing its name to jump straight to it.',
    position: 'bottom',
  },
  {
    targetSelector: '[data-tour="notification-bell"]',
    title: '7. Notifications',
    body: 'A red dot means something new: a file was delivered, a request changed, or your team sent a message. Click the bell to see what happened.',
    position: 'bottom',
  },
  {
    targetSelector: '[data-tour="profile-menu"]',
    title: '8. Your account & password',
    body: 'Click your initials here to change your password, turn on extra login security, or sign out. If you ever forget your password, use "Forgot password" on the sign-in page.',
    position: 'bottom',
  },
  {
    targetSelector: '[data-tour="sidebar-home"]',
    title: "9. You're all set",
    body: "That's the whole app: Home for an overview, Requests for what's needed, and your folders to send and receive files. You can replay this tour any time from your profile menu. Welcome aboard!",
    position: 'right',
  },
];

const STAFF_STEPS: TourStep[] = [
  {
    targetSelector: '[data-tour="sidebar-dashboard"]',
    title: 'Staff dashboard',
    body: 'Your command center: overdue requests, recent uploads, quick stats, and fast access to common actions across all clients.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="sidebar-organizations"]',
    title: 'Organizations',
    body: 'Each client is an isolated workspace. Manage security settings, BAA records, document checklists, and announcements per org.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="sidebar-requests"]',
    title: 'Document requests',
    body: 'Create requests for clients with deadlines, required files, and instructions. Track status on the list or board view.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="upload"]',
    title: 'Upload to clients',
    body: 'Deliver files to any client workspace. Choose the org and folder, then drag files or an entire folder structure.',
    position: 'bottom',
  },
  {
    targetSelector: '[data-tour="sidebar-search"]',
    title: 'Global search',
    body: 'Find any file across all workspaces. Filter by organization, uploader, or date range.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="sidebar-settings"]',
    title: 'Settings',
    body: 'Manage staff users, folder templates, request templates, audit log, and system configuration — all in one place.',
    position: 'right',
  },
  {
    targetSelector: '[data-tour="cmdk"]',
    title: 'Command palette',
    body: 'Press ⌘K (Ctrl+K) to jump anywhere instantly — organizations, folders, requests — without touching the sidebar.',
    position: 'bottom',
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getTooltipStyle(
  rect: Rect,
  position: TourStep['position'],
): React.CSSProperties {
  const gap = 12;
  const tooltipWidth = 300;
  const tooltipHeight = 160;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 12;

  let top: number;
  let left: number;

  switch (position) {
    case 'right':
      top = rect.top;
      left = rect.left + rect.width + gap;
      break;
    case 'left':
      top = rect.top;
      left = rect.left - tooltipWidth - gap;
      break;
    case 'bottom':
      top = rect.top + rect.height + gap;
      left = rect.left;
      break;
    case 'top':
    default:
      top = rect.top - tooltipHeight - gap;
      left = rect.left;
      break;
  }

  // Clamp to viewport
  left = Math.max(pad, Math.min(left, vw - tooltipWidth - pad));
  top = Math.max(pad, Math.min(top, vh - tooltipHeight - pad));

  return { top, left };
}

export function GuidedTour() {
  const { user } = useAuth();
  const showTour = useTourStore((s) => s.showTour);
  const tourStep = useTourStore((s) => s.tourStep);
  const tourCompleted = useTourStore((s) => s.tourCompleted);
  const startTour = useTourStore((s) => s.startTour);
  const nextStep = useTourStore((s) => s.nextStep);
  const dismissTour = useTourStore((s) => s.dismissTour);

  const [targetRect, setTargetRect] = useState<Rect | null>(null);

  const isClient =
    user?.role === 'client_admin' || user?.role === 'client_user';

  const allSteps = useMemo(
    () => (isClient ? CLIENT_STEPS : STAFF_STEPS),
    [isClient],
  );

  // Auto-start on first authenticated visit if not completed
  useEffect(() => {
    if (user && !tourCompleted && !showTour) {
      // Small delay so the DOM is painted
      const t = setTimeout(() => startTour(), 800);
      return () => clearTimeout(t);
    }
  }, [user, tourCompleted, showTour, startTour]);

  // Find visible steps (skip any whose target doesn't exist in the DOM)
  const visibleSteps = useMemo(() => {
    if (!showTour) return [];
    return allSteps.filter(
      (step) => document.querySelector(step.targetSelector) !== null,
    );
  }, [allSteps, showTour, tourStep]); // re-check on step change

  // Current step index within visibleSteps
  const currentVisible = visibleSteps[tourStep] ?? null;
  const totalVisible = visibleSteps.length;

  // If tourStep exceeds available steps, auto-dismiss
  useEffect(() => {
    if (showTour && tourStep >= totalVisible && totalVisible > 0) {
      dismissTour();
    }
  }, [showTour, tourStep, totalVisible, dismissTour]);

  // Measure the target element
  const measureTarget = useCallback(() => {
    if (!currentVisible) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(currentVisible.targetSelector);
    if (!el) {
      setTargetRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setTargetRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    });
  }, [currentVisible]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [measureTarget]);

  if (!showTour || !currentVisible || !targetRect || totalVisible === 0) {
    return null;
  }

  const isLast = tourStep === totalVisible - 1;
  const pad = 6;

  const handleNext = () => {
    if (isLast) {
      dismissTour();
    } else {
      nextStep();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999]" aria-modal="true" role="dialog">
      {/* Dark backdrop with a hole cut out around the target */}
      <div
        className="absolute inset-0"
        style={{
          boxShadow: `0 0 0 9999px rgba(15, 23, 42, 0.55)`,
          top: targetRect.top - pad,
          left: targetRect.left - pad,
          width: targetRect.width + pad * 2,
          height: targetRect.height + pad * 2,
          position: 'fixed',
          pointerEvents: 'none',
        }}
      />

      {/* Clickable backdrop area to dismiss */}
      <div
        className="absolute inset-0"
        onClick={dismissTour}
        style={{ background: 'transparent' }}
      />

      {/* Tooltip card */}
      <div
        className="fixed z-[10000] w-[300px] bg-white border border-slate-200 shadow-lg p-4"
        style={getTooltipStyle(targetRect, currentVisible.position)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-semibold text-bb-navy mb-1">
          {currentVisible.title}
        </div>
        <p className="text-sm text-slate-600 mb-4">{currentVisible.body}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">
            {tourStep + 1} / {totalVisible}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={dismissTour}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Skip tour
            </button>
            <Button variant="primary" onClick={handleNext} className="text-xs">
              {isLast ? 'Done!' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
